/**
 * info@ verwerken — de pagina achter de knop "Verwerken zoals aangegeven" in de dagelijkse info@-samenvatting.
 * Link: /algemene-mailboxen/info/verwerken?o=<token>&c=<categorie>. De link in de mail doet zelf niets (mailscanners
 * volgen links): hier zie je na het inloggen de mails en de actie die nu is ingesteld, en pas "Bevestigen" voert het
 * uit (POST, recht admin_settings of algemene_mailboxen — bewerken). Proef = alleen tonen wat er zou gebeuren.
 * Zelfde bestand op develop en main.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import PageHeader from '../../ui-components/PageHeader';
import { mailboxApi } from '../AdminSettings/algemeneMailboxen/api';

const GRIJS = '#556071';
const RAND = '#e5e9eb';

export const TELLING_LABEL = {
  gearchiveerd: 'gearchiveerd', zou_archiveren: 'zou archiveren', afgehandeld: 'afgehandeld', bevestigd: 'bevestigd',
  blijft_staan: 'overgeslagen', geen_recht: 'geen recht', archief_fout: 'mislukt', niet_gedaan: 'niet verwerkt',
};

/** Terugval als de engine geen telling_tekst meestuurt: "1 overgeslagen, 0 gearchiveerd" bij archiveren
 * (altijd beide getallen), anders vaste volgorde en alleen wat er is. */
export function tellingTekst(telling, actie = '', modus = '') {
  const t = { ...(telling || {}) };
  const delen = [];
  if (actie === 'archiveren') {
    const zou = t.zou_archiveren || 0;
    delen.push(`${t.blijft_staan || 0} overgeslagen`,
      modus === 'dry' ? `${zou} ${zou === 1 ? 'zou' : 'zouden'} gearchiveerd worden` : `${t.gearchiveerd || 0} gearchiveerd`);
    delete t.blijft_staan; delete t.zou_archiveren; if (modus !== 'dry') delete t.gearchiveerd;
  }
  delen.push(...Object.keys(TELLING_LABEL).filter((k) => t[k]).map((k) => `${t[k]} ${TELLING_LABEL[k]}`));
  return delen.join(', ') || 'niets';
}

const telling = (v) => v.telling_tekst || tellingTekst(v.telling, v.actie, v.modus);

/** Kop van de melding na bevestigen. Nooit "Verwerkt" als er bij archiveren niets is verplaatst. */
export function uitkomstKop(u) {
  if (u.status === 'bezig') return 'Nog bezig';
  const niets = u.actie === 'archiveren' && !(u.telling || {}).gearchiveerd;
  if (u.modus === 'dry') return u.al_verwerkt ? 'Proef was al vastgelegd' : 'Proef vastgelegd';
  if (u.al_verwerkt) return niets ? 'Al eerder bevestigd, niets gearchiveerd' : 'Al eerder bevestigd';
  if (niets) return 'Niets gearchiveerd';
  return 'Verwerkt';
}

/** Kleur van de melding: oranje als niet alles lukte of het nog bezig is, groen alleen als er echt iets gebeurde. */
export function uitkomstErnst(u) {
  if (u.status !== 'klaar') return 'warning';
  return uitkomstKop(u) === 'Verwerkt' ? 'success' : 'info';
}

// Statuschip per mail: grijs = overgeslagen, groen = gearchiveerd/klaar, omlijnd = nog te doen, oranje = mislukt.
const CHIP = {
  overgeslagen: { bg: '#eef1f3', color: GRIJS, border: '#eef1f3' },
  gearchiveerd: { bg: '#e6f4ea', color: '#1e6b3a', border: '#e6f4ea' },
  klaar: { bg: '#e6f4ea', color: '#1e6b3a', border: '#e6f4ea' },
  gepland: { bg: 'transparent', color: '#2b3437', border: RAND },
  fout: { bg: '#fdf1e3', color: '#8a4b00', border: '#fdf1e3' },
};

function Status({ status, tekst }) {
  if (!status) return tekst || null;
  const k = CHIP[status.soort] || CHIP.gepland;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
      <Box component="span" data-soort={status.soort} sx={{
        fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.6, px: 0.9, borderRadius: '10px', whiteSpace: 'nowrap',
        bgcolor: k.bg, color: k.color, border: `1px solid ${k.border}`,
      }}>{status.chip}</Box>
      {status.detail ? <span>{status.detail}</span> : null}
    </Box>
  );
}

const RECHT_TEKST = {
  ja: 'De koppeling mag verplaatsen in info@.',
  nee: 'De koppeling mag nog niet verplaatsen in info@ (Mail.ReadWrite ontbreekt).',
  onbekend: 'Of de koppeling mag verplaatsen in info@ is nog niet bekend: het is nog nooit echt geprobeerd.',
};

const tijd = (s) => (s ? new Date(s).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export default function InfoVerwerken() {
  const [params] = useSearchParams();
  const token = params.get('o') || '';
  const groep = params.get('c') || '';
  const [data, setData] = useState(null);
  const [fout, setFout] = useState('');
  const [bezig, setBezig] = useState(false);
  const [uitkomst, setUitkomst] = useState(null);

  // Elke laadactie krijgt een volgnummer: een trager, ouder antwoord overschrijft nooit een nieuwer.
  const volgnr = useRef(0);
  const laad = useCallback(async () => {
    const nr = ++volgnr.current;
    if (!token || !groep) { setData(null); setFout('Deze link is niet compleet.'); return; }
    try {
      const d = await mailboxApi.verwerkenPagina(token, groep);
      if (nr === volgnr.current) { setData(d); setFout(''); }
    } catch (e) { if (nr === volgnr.current) setFout(e.message); }
  }, [token, groep]);
  // Andere link (andere samenvatting of categorie): eerst alles leeg, dan pas opnieuw laden.
  useEffect(() => { setData(null); setUitkomst(null); setFout(''); laad(); }, [laad]);

  // Huidige link; een antwoord op een bevestiging voor een vorige link wordt genegeerd.
  const huidig = useRef('');
  huidig.current = `${token}|${groep}`;

  const bevestigen = async () => {
    // Alleen wat je ziet: de geladen samenvatting/categorie moet die van de adresbalk zijn.
    if (!data || data.token !== token || data.groep !== groep) return;
    const sleutel = `${token}|${groep}`;
    const nogDezelfde = () => huidig.current === sleutel;
    setBezig(true);
    try {
      const uit = await mailboxApi.verwerk(token, groep, data.voorstel?.vingerafdruk || '');
      if (!nogDezelfde()) return;
      setUitkomst(uit);
      await laad();
    } catch (e) {
      if (!nogDezelfde()) return;
      if (e.status === 409) await laad();   // voorstel veranderd: nieuwe stand tonen, opnieuw laten bevestigen
      if (nogDezelfde()) setFout(e.message);
    } finally { setBezig(false); }
  };

  const kop = (
    <PageHeader icon="mark_email_read" title="info@ verwerken"
      subtitle={data ? `${data.label} · uit de dagelijkse info@-samenvatting` : 'Uit de dagelijkse info@-samenvatting'} />
  );
  const omlijst = (inhoud) => (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>{kop}{inhoud}</Box>
  );

  if (fout && !data) return omlijst(<Alert severity="warning">{fout}</Alert>);
  if (!data) return omlijst(<CircularProgress size={20} sx={{ color: '#adb3b5' }} />);

  const vs = data.voorstel || {};
  const proef = vs.modus === 'dry';
  const eerderZelfdeModus = (data.eerder || []).filter((v) => v.modus === vs.modus && ['klaar'].includes(v.status));
  const alGedaan = eerderZelfdeModus.length > 0;
  const kanBevestigen = vs.uitvoerbaar && !data.verlopen && !alGedaan && data.token === token && data.groep === groep;

  return omlijst(
    <>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '0.95rem' }}>
          Voorstel voor <b>{data.label}</b>: {vs.label}.
        </Typography>
        {vs.actie === 'archiveren' && (
          <Typography sx={{ fontSize: '0.8rem', color: GRIJS, mt: 0.5 }}>
            {RECHT_TEKST[data.recht?.recht] || ''} Archiveren verplaatst naar de map "{data.archief_map}", nooit
            verwijderen. Mails met een PO, bijlage of markering worden overgeslagen en blijven in de Inbox voor
            handmatige controle.
          </Typography>
        )}
        {vs.actie === 'afhandelen' && (
          <Typography sx={{ fontSize: '0.8rem', color: GRIJS, mt: 0.5 }}>
            De mails krijgen in de tool de status afgehandeld. In Outlook verandert er niets.
          </Typography>
        )}
        {vs.actie === 'bevestigen' && (
          <Typography sx={{ fontSize: '0.8rem', color: GRIJS, mt: 0.5 }}>
            Sollicitaties worden nooit gearchiveerd of verborgen. Bevestigen legt alleen vast dat je ze gezien hebt.
          </Typography>
        )}
      </Box>

      {data.verlopen && <Alert severity="info" sx={{ mb: 2 }}>Deze samenvatting is te oud om nog te verwerken. Gebruik de knop in een recentere samenvatting.</Alert>}
      {!vs.uitvoerbaar && <Alert severity="warning" sx={{ mb: 2 }}>Dit kan nu niet: {vs.waarom_niet}. Er gebeurt niets.</Alert>}
      {vs.uitvoerbaar && proef && (
        <Alert severity="info" sx={{ mb: 2 }}>Proef: {vs.waarom_niet}. Je ziet hieronder wat er zou gebeuren; bevestigen legt alleen dat vast.</Alert>
      )}
      {fout && <Alert severity="warning" sx={{ mb: 2 }}>{fout}</Alert>}
      {uitkomst && (
        <Alert severity={uitkomstErnst(uitkomst)} sx={{ mb: 2 }}>
          {uitkomstKop(uitkomst)}: {telling(uitkomst)}
          {uitkomst.fout ? ` — gestopt: ${uitkomst.fout}` : ''}.
        </Alert>
      )}

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { borderColor: RAND, fontSize: '0.82rem', py: 1 } }}>
          <TableHead>
            <TableRow>
              {['Ontvangen', 'Afzender', 'Onderwerp', 'Wat er gebeurt'].map((k) => (
                <TableCell key={k} sx={{ color: GRIJS, fontWeight: 600 }}>{k}</TableCell>))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.mails.map((m) => (
              <TableRow key={m.sleutel}>
                <TableCell sx={{ whiteSpace: 'nowrap', color: GRIJS }}>{tijd(m.ontvangen)}</TableCell>
                <TableCell>{m.afzender || m.domein}{m.afzender && m.domein ? <Typography component="span" sx={{ fontSize: '0.74rem', color: GRIJS }}> · {m.domein}</Typography> : null}</TableCell>
                <TableCell>
                  {m.link ? <a href={m.link} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>{m.onderwerp || '(geen onderwerp)'}</a>
                    : (m.onderwerp || <span style={{ color: GRIJS }}>(niet meer in info@)</span>)}
                </TableCell>
                <TableCell sx={{ color: m.gaat_door ? 'inherit' : GRIJS }}>
                  {m.al_gedaan
                    ? <Status status={m.al_gedaan_status} tekst={m.al_gedaan} />
                    : <Status status={m.status} tekst={m.plan} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2.5, flexWrap: 'wrap' }}>
        <Button variant="contained" disableElevation onClick={bevestigen} disabled={!kanBevestigen || bezig}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {bezig ? <CircularProgress size={16} color="inherit" /> : 'Bevestigen'}
        </Button>
        {alGedaan && (
          <Typography sx={{ fontSize: '0.8rem', color: GRIJS }}>
            Al verwerkt door {eerderZelfdeModus[eerderZelfdeModus.length - 1].door?.split('@')[0]} op {tijd(eerderZelfdeModus[eerderZelfdeModus.length - 1].op)}.
          </Typography>
        )}
      </Box>

      {(data.eerder || []).length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>Eerder verwerkt</Typography>
          {data.eerder.map((v) => (
            <Typography key={`${v.op}-${v.modus}`} sx={{ fontSize: '0.8rem', color: GRIJS }}>
              {tijd(v.op)} · {v.door?.split('@')[0]} · {v.modus === 'dry' ? 'proef · ' : ''}{telling(v)}
              {v.status !== 'klaar' ? ` · ${v.status === 'geen_recht' ? 'gestopt: geen recht in info@' : v.status}` : ''}
            </Typography>
          ))}
        </Box>
      )}
    </>,
  );
}
